import React, { useState } from 'react';
import { Button, Divider, Modal, Steps, message } from 'antd';
import { AssignmentStep, BasicInfoStep, PackageStep } from '../';
import { StepperProvider, useStepperContext } from './context';
import {
    IBodyItemResponse,
    IPostAssignPackage,
    IPostItem,
    IProcessPatchTariff,
    NotificationType,
} from '../../../../interfaces';
import { IStepperFormData } from './hooks';
import {
    addNotification,
    apiHandleErrorMessages,
    associateTariffWithPackage,
    getTariff,
    patchTariff,
    pathAssociateTariffWithPackage,
    postTariff,
    removeAssociationBetweenTariffAndPackage,
} from '../../../../middleware';
import { tB, tF, tP, tT, tV } from '../../../../translate';
import TariffsStyles from '../../m_styles.less';

const steps = [
    {
        title: tF('Basic Info'),
        content: <BasicInfoStep />,
    },
    {
        title: tF('Packages'),
        content: <PackageStep />,
        footerTitle: 'Select Packages',
    },
    {
        title: tF('Assignment'),
        content: <AssignmentStep />,
        footerTitle: 'Assign Companies',
    },
];

interface IStepperModal {
    isOpen: boolean;
    closeStepper: () => void;
    initialData?: Partial<IStepperFormData>;
    fetchData?: any;
}

const StepperContent: React.FC<Omit<IStepperModal, 'isOpen'>> = ({
    closeStepper,
    initialData,
    fetchData,
}: Omit<IStepperModal, 'isOpen'>) => {
    const [current, setCurrent] = useState(0);
    const { formData, isSubmitting, getPostTariffData, setSubmitting, resetFormData } = useStepperContext();

    const validateCurrentStep = (): boolean => {
        switch (current) {
            case 0:
                return formData.name.trim() !== '' && !!formData.price && formData.price > 0 && !!formData?.companyId;
            case 1:
                return formData.tariffPackageIds.length > 0;
            case 2:
                return formData.companyIds.length > 0;
            default:
                return true;
        }
    };

    const processPatchTariff = async ({
        initialTariffLocalPackagesIds = [],
        initialTariffRoamingPackagesIds = [],
        tariffLocalPackagesIds = [],
        tariffRoamingPackagesIds = [],
        tariffPackages = [],
        patchData,
    }: IProcessPatchTariff): Promise<IPostItem> => {
        const addRoamingPackagesIds: number[] = tariffRoamingPackagesIds.filter(
            (id) => !initialTariffRoamingPackagesIds.includes(id),
        );
        const removeRoamingPackages: number[] = initialTariffRoamingPackagesIds.filter(
            (id) => !tariffRoamingPackagesIds.includes(id),
        );

        const removedRoamingPackagesIds = tariffPackages
            .filter((p) => removeRoamingPackages.includes(+p.id))
            .filter((p) => p.tariffTariffPackageId)
            .map((p) => Number(p.tariffTariffPackageId));

        if (!initialTariffLocalPackagesIds.length && tariffLocalPackagesIds.length) {
            const body: IPostAssignPackage = {
                tariffId: initialData?.id || '',
                tariffPackageId: tariffLocalPackagesIds[0].toString(),
            };
            const { isError, errors, resp } = await associateTariffWithPackage(body);
            if (isError) {
                if (resp) {
                    apiHandleErrorMessages(resp);
                } else if (errors) {
                    apiHandleErrorMessages((errors as IBodyItemResponse) || {});
                }
            }
        } else if (initialTariffLocalPackagesIds[0] !== tariffLocalPackagesIds[0]) {
            const id = initialData?.tariffPackages?.find((p) => p.packageType === 'local')?.tariffTariffPackageId;
            if (!id) {
                console.error('No tariffTariffPackageId found for the local package');
            } else {
                const body: IPostAssignPackage = {
                    tariffId: initialData?.id || '',
                    tariffPackageId: tariffLocalPackagesIds[0].toString(),
                };
                const { isError, errors, resp } = await pathAssociateTariffWithPackage(id.toString(), body);
                if (isError) {
                    if (resp) {
                        apiHandleErrorMessages(resp);
                    } else if (errors) {
                        apiHandleErrorMessages((errors as IBodyItemResponse) || {});
                    }
                }
            }
        }

        const removedResponses = await Promise.all(
            removedRoamingPackagesIds.map((id) => {
                return removeAssociationBetweenTariffAndPackage(String(id));
            }),
        );

        const addedResponsesRoaming = await Promise.all(
            addRoamingPackagesIds.map((id: any) => {
                const body: IPostAssignPackage = {
                    tariffId: initialData?.id || '',
                    tariffPackageId: id,
                };

                return associateTariffWithPackage(body);
            }),
        );

        const newAddedResponse = addedResponsesRoaming.map((value: IPostItem): boolean => {
            if (value.isError) {
                apiHandleErrorMessages(value.resp || {});
            }

            return value.isError;
        });

        if (removedResponses.includes(true) || newAddedResponse.includes(true)) {
            addNotification({
                type: NotificationType.ERROR,
                message: 'Failed to update tariff packages association. Please try again later.',
            });

            return { isError: true, resp: undefined, errors: undefined };
        }

        return patchTariff(initialData?.id || '', patchData);
    };

    const onSubmit = async () => {
        if (!validateCurrentStep()) {
            message.error(tV('Please fill in all required fields'));
            return;
        }

        setSubmitting(true);

        try {
            const {
                initialTariffLocalPackagesIds,
                initialTariffRoamingPackagesIds,
                tariffPackages,
                tariffLocalPackagesIds,
                tariffRoamingPackagesIds,
                ...postData
            } = getPostTariffData();

            let isErrorCreate: boolean;
            let respCreate: any;

            if (initialData) {
                const { isError, resp } = await processPatchTariff({
                    initialTariffLocalPackagesIds,
                    initialTariffRoamingPackagesIds,
                    tariffLocalPackagesIds,
                    tariffRoamingPackagesIds,
                    tariffPackages,
                    patchData: postData,
                });

                isErrorCreate = isError;
                respCreate = resp;
            } else {
                const { isError, resp } = await postTariff(postData);

                isErrorCreate = isError;
                respCreate = resp;
            }

            if (isErrorCreate) {
                if (respCreate) {
                    return apiHandleErrorMessages(respCreate || {});
                } else {
                    return addNotification({
                        type: NotificationType.ERROR,
                        message: initialData ? 'Failed to update tariff' : 'Failed to create tariff',
                    });
                }
            }

            addNotification({
                type: NotificationType.SUCCESS,
                message: initialData ? 'Tariff successfully updated' : 'Tariff successfully created',
            });

            resetFormData();

            if (initialData) {
                await getTariff(initialData.id || '');
            } else {
                await fetchData();
            }

            closeStepper();
        } catch (error) {
            console.error('Error creating/updating tariff:', error);
            addNotification({
                type: NotificationType.ERROR,
                message: 'An error occurred while creating/updating the tariff',
            });
        } finally {
            setSubmitting(false);
        }
    };

    const next = () => {
        if (!validateCurrentStep()) {
            message.warning('Please complete all required fields before proceeding');
            return;
        }
        setCurrent(current + 1);
    };

    const prev = () => {
        setCurrent(current - 1);
    };

    const handleClose = () => {
        resetFormData();
        setCurrent(0);
        closeStepper();
    };

    const items = steps.map((item) => ({ key: item.title, title: item.title }));

    return (
        <>
            <div className={TariffsStyles.stepperContentTitle}>
                <p>{tT('Create a tariff by selecting packages from the library or creating new ones')}</p>
                <Steps current={current} items={items} />
            </div>
            <Divider />
            <div>{steps[current].content}</div>
            <Divider />
            <div className={TariffsStyles.stepperContentToolbar}>
                <Button onClick={handleClose} disabled={isSubmitting}>
                    {tB('Cancel')}
                </Button>
                {current > 0 && (
                    <Button onClick={prev} disabled={isSubmitting}>
                        {tB('Previous')}
                    </Button>
                )}
                {current < steps.length - 1 && (
                    <Button type="primary" onClick={next} disabled={!validateCurrentStep() || isSubmitting}>
                        {tB('Next')}: {steps[current + 1].footerTitle}
                    </Button>
                )}
                {current === steps.length - 1 && (
                    <Button type="primary" onClick={onSubmit} loading={isSubmitting} disabled={!validateCurrentStep()}>
                        {tB('Submit')}
                    </Button>
                )}
            </div>
        </>
    );
};

export function TariffCreationModal({ isOpen, closeStepper, initialData, fetchData }: IStepperModal) {
    const handleModalCancel = () => {
        Modal.confirm({
            title: tT('Close Dialog?'),
            content: tT('Are you sure you want to close this dialog? Any unsaved changes will be lost.'),
            okText: tB('Yes'),
            cancelText: tB('No'),
            onOk() {
                closeStepper();
            },
            wrapClassName: 'modal',
        });
    };

    return (
        <Modal
            title={tP(initialData ? 'Edit Tariff' : 'Create New Tariff')}
            open={isOpen}
            onCancel={handleModalCancel}
            wrapClassName={'modal'}
            footer={null}
            destroyOnClose
            maskClosable={true}
        >
            <StepperProvider initialData={initialData}>
                <StepperContent closeStepper={closeStepper} initialData={initialData} fetchData={fetchData} />
            </StepperProvider>
        </Modal>
    );
}
